<template>
  <VueFinalModal
    :esc-to-close="true"
    class="flex justify-center items-center"
    content-class="relative flex flex-col w-[400px] mx-auto bg-white border rounded-lg shadow-xl"
  >
    <div class="p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">Record Info</h3>

      <dl class="space-y-3">
        <div class="flex items-center gap-2">
          <dt class="text-sm font-medium text-gray-500">ID:</dt>
          <dd class="flex items-center gap-2">
            <span class="text-sm text-gray-900 font-mono">{{ displayId }}</span>
            <button
              type="button"
              :title="copied ? 'Copied!' : 'Copy to clipboard'"
              class="inline-flex items-center justify-center p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click="copyIdToClipboard"
            >
              <i v-if="!copied" class="fa fa-clipboard" aria-hidden="true"></i>
              <i v-else class="fa fa-check text-green-600" aria-hidden="true"></i>
            </button>
          </dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Created</dt>
          <dd class="mt-0.5 text-sm text-gray-900">{{ formattedCreatedAt }}</dd>
        </div>
        <div v-if="hasPaperTrail && createdBy">
          <dt class="text-sm font-medium text-gray-500">Created by</dt>
          <dd class="mt-0.5 text-sm text-gray-900">{{ createdBy }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Last updated</dt>
          <dd class="mt-0.5 text-sm text-gray-900">{{ formattedUpdatedAt }}</dd>
        </div>
        <div v-if="hasPaperTrail && updatedBy">
          <dt class="text-sm font-medium text-gray-500">Updated by</dt>
          <dd class="mt-0.5 text-sm text-gray-900">{{ updatedBy }}</dd>
        </div>
        <div v-if="discardedAt">
          <dt class="text-sm font-medium text-gray-500">Discarded at</dt>
          <dd class="mt-0.5 text-sm text-gray-900">{{ formattedDiscardedAt }}</dd>
        </div>
        <div v-if="discardedAt">
          <dt class="text-sm font-medium text-gray-500">Discarded by</dt>
          <dd class="mt-0.5 text-sm text-gray-900">{{ discardedBy || '—' }}</dd>
        </div>
      </dl>

      <div v-if="hasPaperTrail && changesUrl" class="mt-4 pt-4 border-t border-gray-200">
        <a
          :href="changesUrl"
          class="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-0 focus:underline"
        >
          View change history
          <svg class="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>

      <div class="mt-6 flex justify-end">
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal"
        >
          Close
        </button>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
  import { computed, ref } from 'vue'
  import { VueFinalModal } from 'vue-final-modal'

  const props = defineProps({
    params: {
      type: Object,
      default: () => ({}),
    },
  })

  const emit = defineEmits(['close'])

  const data = computed(() => props.params?.data || {})

  const displayId = computed(() => {
    const prefix = data.value.entity_prefix || ''
    const id = data.value.record_id
    return prefix ? `${prefix}-${id}` : String(id)
  })

  const formattedCreatedAt = computed(() => {
    const d = data.value.created_at
    if (!d) return '—'
    try {
      const date = new Date(d)
      return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    } catch {
      return d
    }
  })

  const formattedUpdatedAt = computed(() => {
    const d = data.value.updated_at
    if (!d) return '—'
    try {
      const date = new Date(d)
      return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    } catch {
      return d
    }
  })

  const hasPaperTrail = computed(() => data.value.has_paper_trail === true)

  const createdBy = computed(() => data.value.created_by || '')

  const updatedBy = computed(() => data.value.updated_by || '')

  const discardedAt = computed(() => data.value.discarded_at || '')

  const discardedBy = computed(() => data.value.discarded_by || '')

  const formattedDiscardedAt = computed(() => {
    const d = data.value.discarded_at
    if (!d) return '—'
    try {
      const date = new Date(d)
      return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    } catch {
      return d
    }
  })

  const changesUrl = computed(() => data.value.changes_url || '')

  const copied = ref(false)
  let copiedTimeout = null

  async function copyIdToClipboard() {
    try {
      await navigator.clipboard.writeText(displayId.value)
      copied.value = true
      clearTimeout(copiedTimeout)
      copiedTimeout = setTimeout(() => { copied.value = false }, 2000)
    } catch (err) {
      console.warn('Copy failed:', err)
    }
  }

  function closeModal() {
    emit('close')
  }
</script>
